import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, TrendingDown, Sparkles, MapPin } from "lucide-react";

function complianceColor(pct) {
  if (pct >= 90) return "text-emerald-400";
  if (pct >= 75) return "text-[color:var(--gold)]";
  if (pct >= 50) return "text-[color:var(--warning)]";
  return "text-[color:var(--error)]";
}

function ComplianceBar({ pct }) {
  const color = pct >= 90 ? "bg-emerald-400" : pct >= 75 ? "bg-[color:var(--gold)]" : pct >= 50 ? "bg-[color:var(--warning)]" : "bg-[color:var(--error)]";
  return (
    <div className="h-1.5 bg-[color:var(--bg-surface-2)] rounded-full overflow-hidden">
      <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }}/>
    </div>
  );
}

export default function DisplayReports() {
  const [data, setData] = useState({ by_zone: [], by_fandom: [], total_checks: 0 });
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (start) q.set("start", start);
    if (end) q.set("end", end);
    api.get(`/display_reports?${q.toString()}`).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="display-reports">
      <Link to="/display" className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)] text-sm"><ArrowLeft size={14}/> Back to Dashboard</Link>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Reports</div>
          <h1 className="font-heading text-3xl sm:text-4xl">Display Performance</h1>
          <div className="text-sm text-[color:var(--text-secondary)] mt-1">{data.total_checks} check{data.total_checks === 1 ? "" : "s"} analysed</div>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="label-caps">From</label>
            <input type="date" className="hos-input" value={start} onChange={(e) => setStart(e.target.value)} data-testid="reports-start"/>
          </div>
          <div>
            <label className="label-caps">To</label>
            <input type="date" className="hos-input" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="reports-end"/>
          </div>
        </div>
      </div>

      <div className="hos-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={16} className="text-[color:var(--gold)]"/>
          <h2 className="font-heading text-xl">Zone Performance</h2>
          <span className="text-xs text-[color:var(--text-secondary)] ml-2">(ordered worst-first)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left label-caps"><tr><th className="p-2">Zone</th><th>Checks</th><th>Issues</th><th className="min-w-[180px]">Compliance</th></tr></thead>
            <tbody>
              {data.by_zone.map((r) => (
                <tr key={r.zone_id} className="border-t border-[color:var(--border)]" data-testid={`zone-row-${r.zone_id}`}>
                  <td className="p-2 font-semibold">{r.zone_name}</td>
                  <td>{r.checks}</td>
                  <td className={r.issues > 0 ? "text-[color:var(--warning)]" : ""}>{r.issues}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className={`font-heading text-lg ${complianceColor(r.compliance_pct)}`}>{r.compliance_pct}%</span>
                      <div className="flex-1 max-w-[120px]"><ComplianceBar pct={r.compliance_pct}/></div>
                      {r.compliance_pct < 75 && <TrendingDown size={14} className="text-[color:var(--warning)]"/>}
                    </div>
                  </td>
                </tr>
              ))}
              {data.by_zone.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[color:var(--text-secondary)]">No data yet. Complete a few display checks first.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="hos-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-[color:var(--gold)]"/>
          <h2 className="font-heading text-xl">Fandom Performance</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {data.by_fandom.map((r) => (
            <div key={r.fandom_id} className="p-3 border border-[color:var(--border)] rounded-md" data-testid={`fandom-row-${r.fandom_id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold">{r.fandom_name}</span>
                <span className={`font-heading text-lg ${complianceColor(r.compliance_pct)}`}>{r.compliance_pct}%</span>
              </div>
              <ComplianceBar pct={r.compliance_pct}/>
              <div className="text-[11px] text-[color:var(--text-secondary)] mt-1">{r.checks} zone-check{r.checks === 1 ? "" : "s"}</div>
            </div>
          ))}
          {data.by_fandom.length === 0 && (
            <div className="p-6 text-center text-[color:var(--text-secondary)] col-span-full">No fandom data yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
